import React, { useEffect, useMemo, useState, useContext } from "react";
import { Offcanvas, Nav, Form, Button } from "react-bootstrap";
import StarekContext from "../../context/StarekContext";
import { gql, useQuery } from "@apollo/client";

const ENTITY_TYPES_QUERY = gql`
    query EntityType {
        entityTypes {
            id
            code
            singular
            plural
            type
            description
            is_active
        }
    }
`;

const TYPE = {
    PERMISSION: 1, // Read / Add-Modify / Delete
    ASSOCIATIONS: 2, // Link / Unlink
    ACTION: 4, // Execute
};

function entityLabel(et) {
    return et.plural || et.code;
}

const KEYS_BY_TYPE = {
    [TYPE.PERMISSION]: ["read", "modify", "delete"],
    [TYPE.ASSOCIATIONS]: ["link", "unlink"],
    [TYPE.ACTION]: ["execute"],
};

const shapeForType = (t) =>
    Object.fromEntries((KEYS_BY_TYPE[t] || []).map((k) => [k, false]));

const AddRoleSidebar = ({
    show = true,
    mode = "add",
    initialValues = {},
    onClose = () => {},
    onFinish = () => {},
}) => {
    const [activeTab, setActiveTab] = useState("basic");
    const [saving, setSaving] = useState(false);

    const [name, setName] = useState(initialValues.name || "");
    const [description, setDescription] = useState(
        initialValues.description || ""
    );

    const { data, loading, error } = useQuery(ENTITY_TYPES_QUERY);

    const entityTypes = useMemo(
        () => (data?.entityTypes || []).filter((e) => e?.is_active),
        [data]
    );

    const [permissions, setPermissions] = useState({});
    const starekContext = useContext(StarekContext);
    const permissionFlags = starekContext?.types?.permissions?.flags || {};

    const permissionMasks = useMemo(() => {
        const maskRead = permissionFlags.BIT_MASK_READ ?? 1;
        const maskWrite = permissionFlags.BIT_MASK_WRITE ?? 2;
        const maskExec = permissionFlags.BIT_MASK_EXECUTEL ?? 4;
        const maskAll = permissionFlags.BIT_MASK_ALL ?? maskWrite;
        const maskLink =
            permissionFlags.BIT_LOCATION_LINK != null
                ? 1 << permissionFlags.BIT_LOCATION_LINK
                : maskRead;
        const maskUnlink =
            permissionFlags.BIT_LOCATION_UNLINK != null
                ? 1 << permissionFlags.BIT_LOCATION_UNLINK
                : maskWrite;

        return { maskRead, maskWrite, maskExec, maskAll, maskLink, maskUnlink };
    }, [permissionFlags]);

    useEffect(() => {
        setName(initialValues.name || "");
        setDescription(initialValues.description || "");
    }, [initialValues]);

    useEffect(() => {
        if (!entityTypes.length) {
            setPermissions({});
            return;
        }

        const entityById = new Map(entityTypes.map((et) => [et.id, et]));
        const incoming = {};

        // When editing, map role.privileges (bitmask) -> checkbox booleans per entityType
        const rawPrivileges = initialValues?.privileges || [];
        for (const priv of rawPrivileges) {
            const et = entityById.get(priv.entity_type_id);
            if (!et) continue;

            const permValue =
                typeof priv.permission === "number" ? priv.permission : 0;
            const base = shapeForType(et.type);

            if (et.type === TYPE.PERMISSION) {
                // Type 1 → Permissions tab: Read / Add-Modify / Delete
                base.read =
                    (permValue & permissionMasks.maskRead) ===
                    permissionMasks.maskRead;
                base.modify =
                    (permValue & permissionMasks.maskWrite) ===
                    permissionMasks.maskWrite;
                // Delete ties to full-access (ALL) bit
                base.delete =
                    (permValue & permissionMasks.maskAll) ===
                    permissionMasks.maskAll;
            } else if (et.type === TYPE.ASSOCIATIONS) {
                // Type 2 → Associations tab: Link / Unlink
                base.link =
                    (permValue & permissionMasks.maskLink) ===
                    permissionMasks.maskLink;
                base.unlink =
                    (permValue & permissionMasks.maskUnlink) ===
                    permissionMasks.maskUnlink;
            } else if (et.type === TYPE.ACTION) {
                // Type 4 → Actions tab: Execute
                base.execute =
                    (permValue & permissionMasks.maskExec) ===
                        permissionMasks.maskExec ||
                    (permValue & permissionMasks.maskAll) ===
                        permissionMasks.maskAll;
            }

            incoming[et.code] = base;
        }

        setPermissions(() => {
            const next = {};
            for (const et of entityTypes) {
                const base = shapeForType(et.type);
                const incomingForCode = incoming[et.code] || {};
                next[et.code] = { ...base, ...incomingForCode };
            }
            return next;
        });
    }, [entityTypes, initialValues, permissionMasks]);

    const groups = useMemo(() => {
        const g = {
            [TYPE.PERMISSION]: [],
            [TYPE.ASSOCIATIONS]: [],
            [TYPE.ACTION]: [],
        };
        entityTypes.forEach((et) => {
            if (et?.type === TYPE.PERMISSION) g[TYPE.PERMISSION].push(et);
            else if (et?.type === TYPE.ASSOCIATIONS)
                g[TYPE.ASSOCIATIONS].push(et);
            else if (et?.type === TYPE.ACTION) g[TYPE.ACTION].push(et);
        });
        Object.values(g).forEach((arr) =>
            arr.sort((a, b) => entityLabel(a).localeCompare(entityLabel(b)))
        );
        return g;
    }, [entityTypes]);

    const togglePerm = (entityCode, permKey) => {
        setPermissions((prev) => ({
            ...prev,
            [entityCode]: {
                ...prev[entityCode],
                [permKey]: !prev[entityCode]?.[permKey],
            },
        }));
    };

    const submit = async (e) => {
        e?.preventDefault?.();
        if (!name.trim()) return;
        setSaving(true);
        try {
            const normalized = Object.fromEntries(
                entityTypes.map((et) => {
                    const base = shapeForType(et.type);
                    return [
                        et.code,
                        { ...base, ...(permissions[et.code] || {}) },
                    ];
                })
            );

            onFinish({
                name: name.trim(),
                description: description.trim(),
                permissions: normalized,
            });
        } finally {
            setSaving(false);
        }
    };

    const SectionTitle = ({ children }) => (
        <div className="d-flex align-items-center mt-3 mb-1">
            <h6 className="mb-0 text-uppercase text-muted">{children}</h6>
            <hr className="flex-grow-1 ms-2 my-1" />
        </div>
    );

    const TableStyles = () => (
        <style>{`
      .perm-table { width: 100%; table-layout: fixed; border-collapse: separate; border-spacing: 0; }
      .perm-table col.resource { width: auto; }
      .perm-table col.perm { width: 140px; }  /* fixed column width */
      .perm-table thead th { padding: 10px 8px; font-weight: 600; color: #6c757d; }
      .perm-table tbody td { padding: 10px 8px; border-top: 1px solid #eee; }
      .perm-table th.perm, .perm-table td.perm { text-align: center; vertical-align: middle; }
      .perm-table input[type="checkbox"] { display: block; margin: 0 auto; }
    `}</style>
    );

    const PermissionGroupTable = ({ rows }) =>
        !rows?.length ? null : (
            <div className="mb-3">
                <TableStyles />
                <SectionTitle>Permissions</SectionTitle>

                <table className="perm-table">
                    <colgroup>
                        <col className="resource" />
                        <col className="perm" />
                        <col className="perm" />
                        <col className="perm" />
                    </colgroup>
                    <thead>
                        <tr>
                            <th>Resource</th>
                            <th className="perm">Read</th>
                            <th className="perm">Add / Modify</th>
                            <th className="perm">Delete</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((et) => (
                            <tr key={et.id}>
                                <td title={et.description}>
                                    {entityLabel(et)}
                                </td>
                                <td className="perm">
                                    <input
                                        type="checkbox"
                                        checked={Boolean(
                                            permissions[et.code]?.read
                                        )}
                                        onChange={() =>
                                            togglePerm(et.code, "read")
                                        }
                                        aria-label={`${entityLabel(et)} read`}
                                    />
                                </td>
                                <td className="perm">
                                    <input
                                        type="checkbox"
                                        checked={Boolean(
                                            permissions[et.code]?.modify
                                        )}
                                        onChange={() =>
                                            togglePerm(et.code, "modify")
                                        }
                                        aria-label={`${entityLabel(et)} modify`}
                                    />
                                </td>
                                <td className="perm">
                                    <input
                                        type="checkbox"
                                        checked={Boolean(
                                            permissions[et.code]?.delete
                                        )}
                                        onChange={() =>
                                            togglePerm(et.code, "delete")
                                        }
                                        aria-label={`${entityLabel(et)} delete`}
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );

    const RelationGroupTable = ({ rows }) =>
        !rows?.length ? null : (
            <div className="mb-3">
                <TableStyles />
                <SectionTitle>Associations</SectionTitle>
                <table className="perm-table">
                    <colgroup>
                        <col className="resource" />
                        <col className="perm" />
                        <col className="perm" />
                    </colgroup>
                    <thead>
                        <tr>
                            <th>Resource</th>
                            <th className="perm">Link</th>
                            <th className="perm">Unlink</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((et) => (
                            <tr key={et.id}>
                                <td title={et.description}>
                                    {entityLabel(et)}
                                </td>
                                <td className="perm">
                                    <input
                                        type="checkbox"
                                        checked={Boolean(
                                            permissions[et.code]?.link
                                        )}
                                        onChange={() =>
                                            togglePerm(et.code, "link")
                                        }
                                        aria-label={`${entityLabel(et)} link`}
                                    />
                                </td>
                                <td className="perm">
                                    <input
                                        type="checkbox"
                                        checked={Boolean(
                                            permissions[et.code]?.unlink
                                        )}
                                        onChange={() =>
                                            togglePerm(et.code, "unlink")
                                        }
                                        aria-label={`${entityLabel(et)} unlink`}
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );

    const ActionGroupTable = ({ rows }) =>
        !rows?.length ? null : (
            <div className="mb-3">
                <TableStyles />
                <SectionTitle>Actions</SectionTitle>
                <table className="perm-table">
                    <colgroup>
                        <col className="resource" />
                        <col className="perm" />
                    </colgroup>
                    <thead>
                        <tr>
                            <th>Resource</th>
                            <th className="perm">Execute</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((et) => (
                            <tr key={et.id}>
                                <td title={et.description}>
                                    {entityLabel(et)}
                                </td>
                                <td className="perm">
                                    <input
                                        type="checkbox"
                                        checked={Boolean(
                                            permissions[et.code]?.execute
                                        )}
                                        onChange={() =>
                                            togglePerm(et.code, "execute")
                                        }
                                        aria-label={`${entityLabel(
                                            et
                                        )} execute`}
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );

    const headerTitle = useMemo(
        () => (mode === "edit" ? "Edit Role" : "Add Role"),
        [mode]
    );

    return (
        <Offcanvas
            show={show}
            onHide={onClose}
            placement="end"
            backdrop="static"
            style={{ ["--bs-offcanvas-width"]: "40vw" }}
        >
            <Offcanvas.Header closeButton>
                <Offcanvas.Title>{headerTitle}</Offcanvas.Title>
            </Offcanvas.Header>

            <Offcanvas.Body className="pt-0">
                <Nav
                    variant="tabs"
                    activeKey={activeTab}
                    onSelect={(k) => setActiveTab(k || "basic")}
                    className="mb-3"
                >
                    <Nav.Item>
                        <Nav.Link eventKey="basic">Basic Details</Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link eventKey="permissions">Permissions</Nav.Link>
                    </Nav.Item>
                </Nav>

                {activeTab === "basic" && (
                    <Form onSubmit={submit}>
                        <Form.Group className="mb-3">
                            <Form.Label>
                                Name <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                required
                                placeholder="Role name"
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Description</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder="Write a short note…"
                            />
                        </Form.Group>
                    </Form>
                )}

                {activeTab === "permissions" && (
                    <>
                        {loading && (
                            <div className="text-muted">
                                Loading entity types…
                            </div>
                        )}
                        {error && (
                            <div className="text-danger">
                                Failed to load entity types.
                            </div>
                        )}
                        {!loading && !error && (
                            <>
                                <PermissionGroupTable
                                    rows={groups[TYPE.PERMISSION]}
                                />
                                <RelationGroupTable
                                    rows={groups[TYPE.ASSOCIATIONS]}
                                />
                                <ActionGroupTable rows={groups[TYPE.ACTION]} />
                            </>
                        )}
                    </>
                )}
            </Offcanvas.Body>

            <div className="d-flex justify-content-between align-items-center px-3 pb-3">
                <Button variant="outline-secondary" onClick={onClose}>
                    Discard
                </Button>
                <Button
                    variant="primary"
                    onClick={submit}
                    disabled={saving || !name.trim()}
                >
                    {saving ? "Saving…" : "Save & Update"}
                </Button>
            </div>
        </Offcanvas>
    );
};

export default AddRoleSidebar;
